import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  listTools, listCategories, listLocations, listUsers,
  createTool, updateTool, deleteTool, issueTool, lookupToolByCode, exportToolsCSV,
  createCategory, createLocation, requestCategory, requestLocation,
  listCategoryRequests, listLocationRequests,
} from "@/lib/firestore";
import { TOOLS, ISSUE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, MagnifyingGlass, ArrowsClockwise, Wrench, DownloadSimple, QrCode, ArrowSquareOut } from "@phosphor-icons/react";
import QRScanner from "@/components/QRScanner";

const CONDITIONS = ["good", "fair", "poor"];
const NEW_CATEGORY_VALUE = "__new_category__";
const NEW_LOCATION_VALUE = "__new_location__";

export default function Tools() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [showScanner, setShowScanner] = useState(false);

  const [myCatLocRequests, setMyCatLocRequests] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", tool_code: "", category_id: "", location_id: "",
    bin: "", description: "", quantity: 1, condition: "good", image_url: "",
  });

  const [showIssue, setShowIssue] = useState(null); // tool object
  const [issueForm, setIssueForm] = useState({ user_id: "", quantity: 1, due_date: "", notes: "" });

  const load = async () => {
    const data = await listTools({ q, category_id: catFilter, location_id: locFilter });
    setTools(data);
  };

  // Categories/Locations pages are admin-only routes, so this is the only place
  // a non-admin can see whether their quick-create requests were approved.
  const loadMyCatLocRequests = async () => {
    if (isAdmin) return;
    const [catReqs, locReqs] = await Promise.all([listCategoryRequests(user), listLocationRequests(user)]);
    const merged = [
      ...catReqs.map((r) => ({ ...r, kind: "Category" })),
      ...locReqs.map((r) => ({ ...r, kind: "Location" })),
    ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setMyCatLocRequests(merged);
  };

  useEffect(() => {
    (async () => {
      const [c, l] = await Promise.all([listCategories(), listLocations()]);
      setCategories(c);
      setLocations(l);
      if (isAdmin) {
        try {
          const data = await listUsers();
          setUsers(data.filter((u) => u.status === "approved"));
        } catch {}
      }
      await loadMyCatLocRequests();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, catFilter, locFilter]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const locMap = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);

  const openNew = async () => {
    setEditing(null);
    const allTools = await listTools({});
    const nums = allTools
      .map((t) => /^TL-(\d+)$/.exec(t.tool_code || ""))
      .filter(Boolean)
      .map((m) => parseInt(m[1], 10));
    const nextCode = `TL-${String(nums.length ? Math.max(...nums) + 1 : 1).padStart(3, "0")}`;
    setForm({
      name: "", tool_code: nextCode, category_id: categories[0]?.id || "", location_id: locations[0]?.id || "",
      bin: "", description: "", quantity: 1, condition: "good", image_url: "",
    });
    setShowForm(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, tool_code: t.tool_code, category_id: t.category_id, location_id: t.location_id,
      bin: t.bin || "", description: t.description || "", quantity: t.quantity, condition: t.condition, image_url: t.image_url || "",
    });
    setShowForm(true);
  };

  const onCategorySelect = async (e) => {
    const value = e.target.value;
    if (value !== NEW_CATEGORY_VALUE) {
      setForm({ ...form, category_id: value });
      return;
    }
    const name = window.prompt("New category name:");
    if (!name || !name.trim()) return;
    try {
      const created = await createCategory({ name: name.trim(), description: "" }, user);
      setCategories(await listCategories());
      setForm((f) => ({ ...f, category_id: created.id }));
      toast.success("Category created");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onLocationSelect = async (e) => {
    const value = e.target.value;
    if (value !== NEW_LOCATION_VALUE) {
      setForm({ ...form, location_id: value });
      return;
    }
    const name = window.prompt("New location name:");
    if (!name || !name.trim()) return;
    try {
      const created = await createLocation({ name: name.trim(), description: "", bins: [] }, user);
      setLocations(await listLocations());
      setForm((f) => ({ ...f, location_id: created.id }));
      toast.success("Location created");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onCategoryFilterChange = async (e) => {
    const value = e.target.value;
    if (value !== NEW_CATEGORY_VALUE) {
      setCatFilter(value);
      return;
    }
    const name = window.prompt("New category name:");
    if (!name || !name.trim()) return;
    try {
      if (isAdmin) {
        await createCategory({ name: name.trim(), description: "" }, user);
        setCategories(await listCategories());
        toast.success("Category created");
      } else {
        await requestCategory({ name: name.trim() }, user);
        toast.success("Request sent — pending admin approval");
        await loadMyCatLocRequests();
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onLocationFilterChange = async (e) => {
    const value = e.target.value;
    if (value !== NEW_LOCATION_VALUE) {
      setLocFilter(value);
      return;
    }
    const name = window.prompt("New location name:");
    if (!name || !name.trim()) return;
    try {
      if (isAdmin) {
        await createLocation({ name: name.trim(), description: "", bins: [] }, user);
        setLocations(await listLocations());
        toast.success("Location created");
      } else {
        await requestLocation({ name: name.trim() }, user);
        toast.success("Request sent — pending admin approval");
        await loadMyCatLocRequests();
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateTool(editing.id, form);
        toast.success("Tool updated");
      } else {
        await createTool(form, user);
        toast.success("Tool created");
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tool?")) return;
    try {
      await deleteTool(id);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const openIssue = (t) => {
    setIssueForm({ user_id: users[0]?.id || "", quantity: 1, due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), notes: "" });
    setShowIssue(t);
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    try {
      const targetUser = users.find((u) => u.id === issueForm.user_id);
      await issueTool({
        tool_id: showIssue.id,
        user_id: issueForm.user_id,
        quantity: parseInt(issueForm.quantity),
        due_date: issueForm.due_date,
        notes: issueForm.notes,
      }, user, targetUser);
      toast.success(`Tool issued to ${targetUser?.name}`);
      setShowIssue(null);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const onQRScan = async (raw) => {
    setShowScanner(false);
    // Support raw code, the current MTM:code format, and the legacy
    // TOOLCREW:code format (older printed labels still use that prefix).
    const code = raw.startsWith("MTM:")
      ? raw.slice("MTM:".length)
      : raw.startsWith("TOOLCREW:")
      ? raw.slice("TOOLCREW:".length)
      : raw;
    try {
      const data = await lookupToolByCode(code);
      toast.success(`Found: ${data.name}`);
      nav(`/tools/${data.id}`);
    } catch (e) {
      toast.error(`No tool found for code "${code}"`);
    }
  };

  const runExportCSV = async () => {
    try {
      await exportToolsCSV();
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// INVENTORY</div>
          <h1 className="font-display font-black text-4xl">TOOLS</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">{tools.length} tools · {tools.reduce((a, t) => a + (t.quantity || 0), 0)} total units</div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={runExportCSV}
              data-testid="export-tools-csv"
              className="brutal-btn border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
            >
              <DownloadSimple size={16} weight="bold" /> Export CSV
            </button>
            <button
              onClick={() => setShowScanner(true)}
              data-testid="scan-qr-btn"
              className="brutal-btn border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
            >
              <QrCode size={16} weight="bold" /> Scan QR
            </button>
            <button
              data-testid={TOOLS.addBtn}
              onClick={openNew}
              className="brutal-btn bg-[#FF4F00] hover:bg-[#FF6B2B] text-black px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
            >
              <Plus size={16} weight="bold" /> New Tool
            </button>
          </div>
        )}
        {!isAdmin && (
          <button
            onClick={() => setShowScanner(true)}
            data-testid="scan-qr-btn"
            className="brutal-btn border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
          >
            <QrCode size={16} weight="bold" /> Scan QR
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="brutal-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <input
              data-testid={TOOLS.searchInput}
              placeholder="Search name, code, description..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-[#0A0A0C] border border-[#27272A] pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
            />
          </div>
          <select
            data-testid={TOOLS.categoryFilter}
            value={catFilter}
            onChange={onCategoryFilterChange}
            className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
          >
            <option value="">All categories</option>
            <option value={NEW_CATEGORY_VALUE}>+ Create new category</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            data-testid={TOOLS.locationFilter}
            value={locFilter}
            onChange={onLocationFilterChange}
            className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]"
          >
            <option value="">All locations</option>
            <option value={NEW_LOCATION_VALUE}>+ Create new location</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      </div>

      {!isAdmin && myCatLocRequests.length > 0 && (
        <div className="brutal-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-display font-black text-lg">MY CATEGORY / LOCATION REQUESTS</h2>
          </div>
          <div className="space-y-2">
            {myCatLocRequests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1a1e] pt-2 first:border-t-0 first:pt-0">
                <div className="text-sm">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#52525B] mr-2">{r.kind}</span>
                  <span className="font-semibold">{r.name}</span>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  r.status === "pending" ? "border-[#FFD700] text-[#FFD700]" :
                  r.status === "approved" ? "border-[#00E676] text-[#00E676]" :
                  "border-[#D32F2F] text-[#D32F2F]"
                }`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// {editing ? "EDIT" : "NEW"} TOOL</div>
                <h2 className="font-display font-black text-2xl">{editing ? "Update Tool" : "Add Tool"}</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input data-testid={TOOLS.nameInput} required placeholder="Tool name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <input data-testid={TOOLS.codeInput} required placeholder="Tool code (e.g. TL-001)" value={form.tool_code} onChange={(e) => setForm({ ...form, tool_code: e.target.value })} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <select data-testid={TOOLS.categorySelect} required value={form.category_id} onChange={onCategorySelect} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                <option value="">Select category</option>
                <option value={NEW_CATEGORY_VALUE}>+ Create new category</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select data-testid={TOOLS.locationSelect} required value={form.location_id} onChange={onLocationSelect} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                <option value="">Select location</option>
                <option value={NEW_LOCATION_VALUE}>+ Create new location</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <input data-testid={TOOLS.binInput} placeholder="Bin code (e.g. A1)" value={form.bin} onChange={(e) => setForm({ ...form, bin: e.target.value })} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <input data-testid={TOOLS.qtyInput} type="number" min="0" required placeholder="Quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "0") })} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <select data-testid={TOOLS.conditionSelect} value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid={TOOLS.imageInput} placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <textarea data-testid={TOOLS.descInput} placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2 bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <div className="md:col-span-2 flex gap-2 mt-2">
                <button type="submit" data-testid={TOOLS.saveBtn} className="brutal-btn bg-[#FF4F00] text-black px-4 py-2.5 hover:bg-[#FF6B2B] transition-colors duration-150">
                  {editing ? "Update Tool" : "Create Tool"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="brutal-btn border border-[#27272A] px-4 py-2.5 hover:border-[#FF4F00] transition-colors duration-150">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue modal */}
      {showIssue && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowIssue(null)}>
          <div onClick={(e) => e.stopPropagation()} className="brutal-card p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="font-mono text-xs text-[#FF4F00] tracking-widest">// ISSUE TOOL</div>
                <h2 className="font-display font-black text-2xl">{showIssue.name}</h2>
                <div className="font-mono text-xs text-[#A1A1AA]">#{showIssue.tool_code} · available: {showIssue.available_quantity}</div>
              </div>
              <button onClick={() => setShowIssue(null)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submitIssue} className="space-y-3">
              <select data-testid={ISSUE.userSelect} required value={issueForm.user_id} onChange={(e) => setIssueForm({ ...issueForm, user_id: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]">
                <option value="">Assign to user...</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
              </select>
              <input data-testid={ISSUE.qtyInput} type="number" min="1" max={showIssue.available_quantity} required placeholder="Quantity" value={issueForm.quantity} onChange={(e) => setIssueForm({ ...issueForm, quantity: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <input data-testid={ISSUE.dueDateInput} type="date" required value={issueForm.due_date} onChange={(e) => setIssueForm({ ...issueForm, due_date: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 font-mono focus:outline-none focus:border-[#FF4F00]" />
              <textarea data-testid={ISSUE.notesInput} rows={2} placeholder="Notes (optional)" value={issueForm.notes} onChange={(e) => setIssueForm({ ...issueForm, notes: e.target.value })} className="w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00]" />
              <button type="submit" data-testid={ISSUE.submitBtn} className="brutal-btn w-full bg-[#FF4F00] text-black px-4 py-3 hover:bg-[#FF6B2B] transition-colors duration-150">
                Confirm Issue →
              </button>
            </form>
          </div>
        </div>
      )}

      {/* QR Scanner */}
      {showScanner && <QRScanner onScan={onQRScan} onClose={() => setShowScanner(false)} />}

      {/* Tools grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tools.length === 0 && (
          <div className="col-span-full brutal-card p-10 text-center text-[#52525B] font-mono">
            // no tools in inventory — {isAdmin ? "add one to get started" : "ask your admin to add tools"} //
          </div>
        )}
        {tools.map((t) => {
          const cat = catMap[t.category_id];
          const loc = locMap[t.location_id];
          const availPct = t.quantity > 0 ? (t.available_quantity / t.quantity) * 100 : 0;
          return (
            <div key={t.id} data-testid={`tool-card-${t.id}`} className="brutal-card p-5 relative">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <Wrench size={14} weight="bold" className="text-[#FF4F00]" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4F00]">{cat?.name || "TOOL"}</div>
                </div>
                <div className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  t.condition === "good" ? "border-[#00E676] text-[#00E676]" :
                  t.condition === "fair" ? "border-[#FFD700] text-[#FFD700]" :
                  "border-[#D32F2F] text-[#D32F2F]"
                }`}>{t.condition}</div>
              </div>
              <div className="font-display font-bold text-xl leading-tight mb-1 flex items-center gap-2">
                <span className="flex-1">{t.name}</span>
                <Link
                  to={`/tools/${t.id}`}
                  data-testid={`view-tool-${t.id}`}
                  className="text-[#52525B] hover:text-[#FF4F00] transition-colors duration-150"
                  title="View details"
                >
                  <ArrowSquareOut size={16} weight="bold" />
                </Link>
              </div>
              <div className="font-mono text-xs text-[#52525B] mb-3">#{t.tool_code}</div>
              {t.description && <div className="text-sm text-[#A1A1AA] mb-3 line-clamp-2">{t.description}</div>}

              <div className="grid grid-cols-2 gap-2 text-xs mb-3 border-t border-[#1a1a1e] pt-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Location</div>
                  <div className="text-[#F5F5F5]">{loc?.name || "—"}{t.bin ? ` · ${t.bin}` : ""}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">Available</div>
                  <div className="font-mono text-[#FF4F00] font-bold">{t.available_quantity}<span className="text-[#52525B]">/{t.quantity}</span></div>
                </div>
              </div>

              <div className="h-1 bg-[#1a1a1e] mb-3">
                <div className="h-full bg-[#FF4F00]" style={{ width: `${availPct}%` }} />
              </div>

              <div className="flex gap-2">
                {isAdmin && t.available_quantity > 0 && (
                  <button
                    data-testid={TOOLS.issueBtn(t.id)}
                    onClick={() => openIssue(t)}
                    className="brutal-btn flex-1 bg-[#FF4F00] text-black text-xs py-2 hover:bg-[#FF6B2B] transition-colors duration-150 flex items-center justify-center gap-1"
                  >
                    <ArrowsClockwise size={14} weight="bold" /> Issue
                  </button>
                )}
                {isAdmin && (
                  <>
                    <button
                      data-testid={TOOLS.editBtn(t.id)}
                      onClick={() => openEdit(t)}
                      className="w-9 border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] flex items-center justify-center transition-colors duration-150"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      data-testid={TOOLS.deleteBtn(t.id)}
                      onClick={() => del(t.id)}
                      className="w-9 border border-[#27272A] hover:border-[#D32F2F] hover:text-[#D32F2F] flex items-center justify-center transition-colors duration-150"
                    >
                      <Trash size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
